import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import classNames from 'classnames';
import { useSubscribedProfiles } from '#network/Billing';
import AppModals from '#app/containers/AppModals';
import { FormattedMessage } from 'react-intl';
import ServiceStatusBanner from '#components/Elements/ServiceStatusBanner/ServiceStatusBanner';
import HeaderTitle from '#components/Elements/HeaderTitle/HeaderTitle';
import MainToolbar from '#components/Layouts/Site/MainToolbar/MainToolbar';
import NavigationBar from '#components/Layouts/Site/NavigationBar/NavigationBar';
import Icon from '#components/Elements/Icon/Icon';
import Button from '#components/Elements/Button/Button';
import { ErrorHandlingContext } from '#modules/context/ErrorHandlingContext';
import { useAnyProviderLoggedIn, useActiveEntity } from '#modules/api/User';
import { useKycRedirect } from '#modules/hooks/navigation';
import { KYC_STATUS_NONE } from '#modules/utils/kyc';
import Maintenance from '#components/Pages/Maintenance/Maintenance';
import PlatformLogo from '#components/Elements/PlatformLogo/PlatformLogo';
import FooterNav from '#components/Elements/FooterNav/FooterNav';
import styles from './DefaultLayout.scss';

export default function DefaultLayout({
  children,
  showUnverifiedNavigation = false,
  showNavigation = true,
  forceModalContent,
  allowModalBackgroundInteraction,
  showAppVersionOverForcedModal,
  enableServiceBanner,
  modalTest,
  pageTest,
  maxHeightModal,
  checkVerification = false,
  showCurrencyPicker = false,
}) {
  const { networkError } = React.useContext(ErrorHandlingContext);
  const location = useLocation();
  const kycRedirect = useKycRedirect();
  const { settings } = queryString.parse(location.search) || {};

  const isLoggedIn = useAnyProviderLoggedIn();
  const profiles = useSubscribedProfiles();
  const entity = useActiveEntity();
  const isUnverified = entity?.checked === false;

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  function handleToggleMenu() {
    setIsMenuOpen(!isMenuOpen);
  }
  if (isLoggedIn && !profiles?.length) {
    showNavigation = false;
    children = null;
    allowModalBackgroundInteraction = !settings;
    showAppVersionOverForcedModal = true;
    forceModalContent = null;
  }

  if (networkError) {
    forceModalContent = <Maintenance />;
  }

  const redirectToVerification = () => {
    kycRedirect(KYC_STATUS_NONE);
  };

  return (
    <>
      <div className={styles.layoutContainer}>
        {!!(showNavigation || !!showUnverifiedNavigation) && (
          <>
            <MainToolbar
              isMenuOpen={isMenuOpen}
              setIsMenuOpen={setIsMenuOpen}
            />
            {!!showNavigation && (
              <div className={styles.desktop}>
                <NavigationBar />
              </div>
            )}
          </>
        )}
        <div
          className={classNames(
            styles.mainContainer,
            !children && styles.noPageContent,
            children && 'table-with-floating-header',
          )}
          data-test={pageTest}
        >
          <div className={styles.responsive}>
            <div className={styles.mobileHeader}>
              {!!showNavigation && !isMenuOpen && (
                <div className={styles.mobile}>
                  <div>
                    <Button
                      className={styles.menuButton}
                      onClick={handleToggleMenu}
                    >
                      <Icon
                        className={styles.menuIcon}
                        icon={'fa fa-bars'}
                        size="large"
                      />
                    </Button>
                  </div>
                </div>
              )}
              {!showNavigation && <div />}
              {!isMenuOpen && (
                <>
                  <HeaderTitle showCurrencyPicker={showCurrencyPicker}>
                    <PlatformLogo leftAligned={showCurrencyPicker} />
                  </HeaderTitle>
                  <div />
                </>
              )}
              {isMenuOpen && (
                <div className={styles.closeButton}>
                  <Button
                    className={styles.menuButton}
                    onClick={handleToggleMenu}
                  >
                    <Icon
                      className={styles.menuIcon}
                      icon={isMenuOpen ? 'fa fa-times' : 'fa fa-bars'}
                      size="large"
                    />
                  </Button>
                </div>
              )}
            </div>
            {isMenuOpen && (
              <div
                className={classNames(
                  'menu-open',
                  isMenuOpen && styles.visibleMenu,
                )}
              >
                <NavigationBar />
              </div>
            )}
            {checkVerification && isUnverified && !isMenuOpen && (
              <div
                className={styles.unverifiedAccountNotification}
                onClick={redirectToVerification}
                onKeyDown={redirectToVerification}
                role="button"
                tabIndex={0}
              >
                <div className={styles.unverifiedText}>
                  <span>
                    <FormattedMessage defaultMessage="Almost there!" />
                    <br />
                    <FormattedMessage defaultMessage="We just need to verify your identity — a quick step required for all platform services to keep things safe and secure." />
                    <br />
                    <br />
                    <u>
                      <FormattedMessage defaultMessage="Click here to proceed" />
                    </u>
                  </span>
                </div>
              </div>
            )}
            {!isMenuOpen && children}
            {!isMenuOpen && <div className={styles.bottomPadding} />}
            {!!showNavigation && !isMenuOpen && <FooterNav />}
          </div>
        </div>
      </div>
      {!!enableServiceBanner && <ServiceStatusBanner />}
      <AppModals
        test={modalTest}
        forceModalContent={forceModalContent}
        allowBackgroundInteraction={allowModalBackgroundInteraction}
        showAppVersionOverForcedModal={showAppVersionOverForcedModal}
        maxHeightModal={maxHeightModal}
      />
    </>
  );
}
